import './AttachmentsTable.scss';
import { UseFormReturn } from 'react-hook-form';
import { IDocumentAttachmentsTable } from '@models/Forms/IForms';
import { IFieldElem } from '@models/IFormData';
import React, { useContext, useEffect, useState } from 'react';
import { TableTab } from '@models/document/IDocumentScheme';
import DocumentAttachTable from '@organisms/document/DocumentTabBuilder/tabs/DocumentTabAttach/DocumentAttachTable';
import { IAttachesCreateModel } from '@models/attaches/IAttachesCreateModel';
import { AttachActionsService, IAttachPropertyField } from '@services/actions/AttachActionsService';
import { ISelectedFiles } from '@molecules/AttachFilesBlock/ISelectedFiles';
import { useTypedSelector } from '@hooks/useTypedSelector';
import AttachFilesBlockControl from '@controls/AttachFilesBlockControl';
import { ConfigService } from '@/configuration/services/configService';
import { DocumentModalContext } from '@/context/Contexts';
import Hint from '@srm/ui/hint';
import AttachFilesBlockSkeleton from '@molecules/AttachFilesBlock/AttachFilesBlockSkeleton';
import { useFormulaState } from '@molecules/formbuilder/hooks';
import { ATTACHMENTS_FORM_PATH, getAttachmentsAnchorId } from '@utils/validation/document/attachmentsValidation';

export interface IAttachmentsTableProps<TFieldValues extends object = object> {
    table: IDocumentAttachmentsTable;
    tables: TableTab[];
    formMethods: UseFormReturn<TFieldValues>;
    isEdit: boolean;
    isNew: boolean;
    fields: Record<string, IFieldElem>;
    docId?: string;
}

const AttachmentsTable = <TFieldValues extends object = object>({
    table,
    tables,
    formMethods,
    isEdit,
    isNew,
    fields,
    docId,
}: IAttachmentsTableProps<TFieldValues>) => {
    const activated = React.useRef(false);
    const loadedData = React.useRef(false);
    const [attachModel, setAttachModel] = useState<IAttachesCreateModel>();
    const [loadFailed, setLoadFailed] = useState(false);
    const globalData = useTypedSelector((state) => state.document.data);

    const documentModalContext = useContext(DocumentModalContext);

    useEffect(() => {
        activated.current = true;
        return () => {
            activated.current = false;
        };
    }, []);

    useEffect(() => {
        if (isNew || isEdit) initData();
    }, [globalData, documentModalContext.data]);

    const initData = () => {
        const data = documentModalContext.data ?? globalData;
        let id = data?.id;
        let flowKey = data?.flowKey;
        if (id && flowKey) {
            let service = new AttachActionsService(id, flowKey);
            if (table.addButtonKey) {
                service.getAttachInfo(table.addButtonKey).then(
                    (res) => {
                        if (activated.current) {
                            setAttachModel(res.data);
                            setLoadFailed(false);
                        }

                        if (isEdit) {
                            let props: IAttachPropertyField[] = res.data.properties.map((x) => {
                                return { key: x.key, fieldType: x.fieldType ?? 'property' };
                            });
                            service.getAttachmentsProperties(props).then((response) => {
                                let keyColl = Object.keys(response.data);
                                let rows: any[] = [];
                                for (let index = 0; index < keyColl.length; index++) {
                                    const attachKey = keyColl[index];
                                    let attachPropColl = (response.data as any)[attachKey];
                                    let propKeys = Object.keys(attachPropColl);

                                    let row: any = {
                                        _fileId: attachKey,
                                        _actType: 'edit',
                                    };
                                    for (let y = 0; y < propKeys.length; y++) {
                                        const propKey = propKeys[y];
                                        let prop = (attachPropColl as any)[propKey];
                                        row[propKey] = prop.value;
                                    }

                                    rows.push(row);
                                }
                                let data: ISelectedFiles = {
                                    files: [],
                                    attachProperties: rows,
                                };

                                formMethods.setValue('files' as any, data as any, { shouldDirty: false });
                                loadedData.current = true;
                            });
                        }
                    },
                    () => {
                        if (activated.current) setLoadFailed(true);
                    },
                );
            }
        }
    };

    useEffect(() => {
        if (!isNew && !isEdit) loadedData.current = false; // Нажата кнопка "Отмена"
        if (isNew || isEdit) initData();
    }, [isEdit, isNew]);

    // Обязательность блока — формула конфигурации; под resolver-валидацией её считает
    // resolver, но контрол показывает маркер требования и в легаси-режиме.
    const required = useFormulaState(table.required, fields, formMethods, isEdit, isNew, activated, {
        fallback: false,
    });

    const attachmentTable = tables.find((t) => t.key === table.key);

    return (
        // Якорь перехода панели «Заполнение» и ошибки сохранения: у блока вложений нет
        // записи в doc.data.fields, поэтому адресуется он не индексом поля, а этим атрибутом.
        <div data-document-anchor={getAttachmentsAnchorId(table)}>
            {table && table.key && docId && attachmentTable && !isEdit && !isNew ? (
                <DocumentAttachTable id={table.id} attachTable={attachmentTable} docId={docId} />
            ) : attachModel ? (
                <AttachFilesBlockControl
                    id={table.id}
                    attachModel={attachModel}
                    formMethods={formMethods}
                    name={ATTACHMENTS_FORM_PATH}
                    required={required}
                    requiredMessage={table.requiredMessage}
                    // Черновик хранит реквизиты и таблицы; выбранные файлы уедут только вместе с документом.
                    note={
                        isNew && ConfigService.get().application.autoSaveDocument
                            ? 'Файлы сохранятся вместе с документом'
                            : undefined
                    }
                />
            ) : loadFailed || !table.addButtonKey ? (
                <Hint
                    role="alert"
                    title={`Не удалось загрузить конфигурацию таблицы загрузки файлов: ${table.key}`}
                    tone="danger"
                    maxWidth="100%"
                />
            ) : (
                <AttachFilesBlockSkeleton id={table.id} />
            )}
        </div>
    );
};

export default AttachmentsTable;
